/**
 * content.ts — Canonical book content types
 *
 * Single source of truth for the scraper, translation, renderer, and
 * validation workstreams. The runtime-validatable mirror lives in
 * schema/book.schema.json.
 */

export const SCHEMA_VERSION = "1" as const;
export type SchemaVersion = typeof SCHEMA_VERSION;

export type Lang = "de" | "en" | "it";
export type PageSide = "left" | "right";
export type PageDisplayLang = "de" | "it" | "both";

export type PageType =
  | "cover"
  | "front-matter"
  | "imprint"
  | "toc"
  | "text-spread"
  | "image-only"
  | "exercise"
  | "blank";

/** One translatable text unit (paragraph, caption, label). */
export interface TextBlock {
  de: string;
  en?: string;
  it?: string;
}

export interface RemoteImage {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

/** German-language audio retained from the source book. */
export interface AudioRef {
  url: string;
  lang: "de";
  label?: string;
}

interface OverlayBase {
  x: number;
  y: number;
  unit: "percent" | "px";
}

export interface OverlayAudio extends OverlayBase {
  type: "audio";
  audioRef: AudioRef;
}

export interface OverlayBadge extends OverlayBase {
  type: "badge";
}

export type Overlay = OverlayAudio | OverlayBadge;

export interface TocEntry {
  anchor: string;
  spreadIndex: number;
  labelDe: string;
  labelIt?: string;
}

export interface Page {
  pageNumber: number | null;
  side: PageSide;
  pageType: PageType;
  displayLang: PageDisplayLang;
  images: RemoteImage[];
  textBlocks: TextBlock[];
  audio: AudioRef[];
  overlays: Overlay[];
  tocAnchor?: string;
  showTranslatedBadge: boolean;
}

export interface Spread {
  spreadIndex: number;
  left: Page | null;
  right: Page | null;
  spreadType: PageType;
}

export interface BookManifest {
  schemaVersion: SchemaVersion;
  title: TextBlock;
  totalPages: number;
  spreads: Spread[];
  toc: TocEntry[];
  sourceUrl: string;
  extractedAt?: string;
}

// Backward-compatible aliases used by the scaffold and renderer workstreams.
export type AudioAsset = AudioRef;
export type BookPage = Page;
export type BookContent = BookManifest;

export function loadBookContent(): Promise<BookManifest> {
  return fetch("/book-manifest.json")
    .then((res) => {
      if (!res.ok) throw new Error(`book-manifest.json HTTP ${res.status}`);
      return res.json() as Promise<BookManifest>;
    })
    .catch((err) => {
      console.warn("[libro-leone] Could not load book-manifest.json:", err);
      return {
        schemaVersion: SCHEMA_VERSION,
        title: {
          de: "Der allerbeste Spielplatz der Welt",
          en: "The Very Best Playground in the Whole Wide World",
        },
        totalPages: 95,
        spreads: [],
        toc: [],
        sourceUrl:
          "https://www.mulingula-praxis.de/buch/der-allerbeste-spielplatz-der-welt/sprachen/englisch/",
      } satisfies BookManifest;
    });
}
